import type { GoToNextStepOnClickOf } from '@unifyapps/defs/types/page/walkthrough';
import { useEffect } from 'react';

/**
 * Sets up click handlers to advance the walkthrough step:
 * - When goToNextStepOnClickOf === 'element': listen on the target element.
 * - When goToNextStepOnClickOf === 'outside': listen on the SVG path (backdrop).
 * Relies on the overlay path using visiblePainted so clicks in the hole reach the target.
 * Takes pathElement (not a ref) so the effect re-runs when the path mounts.
 */
export function useWalkthroughStepClickHandlers(
  targetElement: HTMLElement | null,
  pathElement: SVGPathElement | null,
  goToNextStepOnClickOf: GoToNextStepOnClickOf | undefined,
  showBackdrop: boolean,
  moveStep: (direction: 'next' | 'previous') => void | Promise<void>,
): void {
  // Handle click on target element to advance step
  // When goToNextStepOnClickOf === 'element', we listen directly on the target element
  // The SVG path with visiblePainted allows clicks to pass through the hole to reach the target
  useEffect(() => {
    if (!targetElement || goToNextStepOnClickOf !== 'element') {
      return;
    }

    const handleElementClick = (e: MouseEvent) => {
      e.stopPropagation();
      void moveStep('next');
    };

    targetElement.addEventListener('click', handleElementClick, false);

    return () => {
      targetElement.removeEventListener('click', handleElementClick, false);
    };
  }, [targetElement, goToNextStepOnClickOf, moveStep]);

  // Handle click on backdrop/overlay to advance step
  // When goToNextStepOnClickOf === 'outside', the path with visiblePainted captures backdrop clicks
  // while allowing clicks on the hole to pass through to the target element.
  // Depends on pathElement (reactive state) so the effect re-runs when the path mounts.
  useEffect(() => {
    if (goToNextStepOnClickOf !== 'outside' || !showBackdrop || !pathElement) {
      return;
    }

    const handleBackdropClick = (e: MouseEvent) => {
      // Only advance if clicking the SVG path itself (the painted backdrop area)
      // Clicks on the hole pass through to the target element and won't trigger this
      if (e.target === pathElement) {
        void moveStep('next');
      }
    };

    pathElement.addEventListener('click', handleBackdropClick, false);

    return () => {
      pathElement.removeEventListener('click', handleBackdropClick, false);
    };
  }, [goToNextStepOnClickOf, showBackdrop, pathElement, moveStep]);
}
